import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Send, CheckCircle, MessageSquare, User, Building, Briefcase, Mail } from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const SubmitReview = () => {
    const [formData, setFormData] = useState({
        clientName: '',
        company: '',
        email: '',
        role: '',
        projectType: '',
        rating: 5,
        review: '',
        agreeToPublish: false,
        website: '' // Alien field for honeypot
    });

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const projectTypes = [
        'Web Development',
        'AI Automation',
        'Custom Internal Tools',
        'Cloud Integration',
        'Consulting',
        'Other'
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleRatingClick = (rating) => {
        setFormData(prev => ({
            ...prev,
            rating
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.clientName || !formData.projectType || !formData.review) {
            alert('Please fill in all required fields.');
            return;
        }

        if (!formData.agreeToPublish) {
            alert('Please agree to allow us to publish your review.');
            return;
        }

        setIsSubmitting(true);

        // Honeypot check
        if (formData.website) {
            console.log('Bot detected via honeypot');
            setTimeout(() => {
                setIsSubmitted(true);
                setIsSubmitting(false);
            }, 1000);
            return;
        }

        try {
            // Prepare Netlify encoded form payload
            const encoded = new URLSearchParams({
                "form-name": "testimonial-submission",
                ...formData,
            }).toString();

            // Submit to Netlify
            await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encoded,
            });

            console.log('=== REVIEW SUBMITTED ===');
            console.log('Data:', formData);
            console.log('=== END ===');

            setIsSubmitted(true);
        } catch (error) {
            console.error('Error submitting review:', error);
            alert(error.message || 'Failed to submit review. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 mt-20">
                <Header />
                <main className="flex-grow flex items-center justify-center px-4 py-12 md:py-20">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="max-w-2xl w-full text-center"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                            className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 mb-8 shadow-xl"
                        >
                            <CheckCircle className="w-12 h-12 text-white" />
                        </motion.div>

                        <h1 className="text-4xl md:text-5xl 3xl:text-6xl font-bold mb-6 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent">
                            Thank You!
                        </h1>
                        <p className="text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl text-slate-600 mb-8 leading-relaxed">
                            Your review has been submitted successfully. We truly appreciate you taking the time to share your experience with us.
                        </p>
                        <motion.a
                            href="/"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all"
                        >
                            Back to Home
                        </motion.a>
                    </motion.div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 mt-20">
            <Header />

            <main className="flex-grow py-8 md:py-16 lg:py-20 3xl:py-32">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
                    <div className="max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl 5xl:max-w-6xl 6xl:max-w-7xl mx-auto">
                        {/* Header */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mb-8 md:mb-12 3xl:mb-16"
                        >
                            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 3xl:w-24 3xl:h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 mb-4 md:mb-6 3xl:mb-8 shadow-xl">
                                <MessageSquare className="w-8 h-8 md:w-10 md:h-10 3xl:w-12 3xl:h-12 text-white" />
                            </div>

                            <h1 className="text-3xl md:text-4xl lg:text-5xl 3xl:text-6xl font-bold mb-4 md:mb-6 3xl:mb-8 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-normal">
                                Share Your Experience
                            </h1>
                            <p className="text-base md:text-lg lg:text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl text-slate-600 leading-relaxed px-4">
                                We’d love to hear about your experience working with us. Your feedback helps us improve and also helps others make confident decisions. Thank you for taking a moment to share your thoughts.
                            </p>
                        </motion.div>

                        {/* Form */}
                        <motion.form
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            onSubmit={handleSubmit}
                            className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-6 md:p-10 lg:p-12 3xl:p-16 border border-slate-100"
                            name="testimonial-submission"
                            method="POST"
                            data-netlify="true"
                            data-netlify-honeypot="website"
                        >
                            <input type="hidden" name="form-name" value="testimonial-submission" />
                            <div className="space-y-6 3xl:space-y-8">

                                {/* 1. Full Name */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-semibold text-slate-700 mb-2">
                                        <User className="w-4 h-4 3xl:w-5 3xl:h-5 text-blue-600" />
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="clientName"
                                        value={formData.clientName}
                                        onChange={handleChange}
                                        required
                                        placeholder="e.g., Rajesh Kumar"
                                        className="w-full px-4 py-3 3xl:px-5 3xl:py-4 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none text-slate-900"
                                    />
                                </div>

                                {/* 2. Company Name */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-semibold text-slate-700 mb-2">
                                        <Building className="w-4 h-4 3xl:w-5 3xl:h-5 text-blue-600" />
                                        Company Name (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        name="company"
                                        value={formData.company}
                                        onChange={handleChange}
                                        placeholder="e.g., TechVision Solutions"
                                        className="w-full px-4 py-3 3xl:px-5 3xl:py-4 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none text-slate-900"
                                    />
                                    <p className="text-xs 3xl:text-sm text-slate-500 mt-1">Helps when B2B clients submit reviews</p>
                                </div>

                                {/* 3. Email Address */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-semibold text-slate-700 mb-2">
                                        <Mail className="w-4 h-4 3xl:w-5 3xl:h-5 text-blue-600" />
                                        Email Address (Optional — Internal Use Only, Not Displayed)
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="your.email@company.com"
                                        className="w-full px-4 py-3 3xl:px-5 3xl:py-4 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none text-slate-900"
                                    />
                                    <p className="text-xs 3xl:text-sm text-slate-500 mt-1">For verification if needed, but won’t show on website</p>
                                </div>

                                {/* 4. Role / Position */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-semibold text-slate-700 mb-2">
                                        <Briefcase className="w-4 h-4 3xl:w-5 3xl:h-5 text-blue-600" />
                                        Your Role / Position (optional)
                                    </label>
                                    <input
                                        type="text"
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        placeholder="e.g., CEO, CTO, Manager"
                                        className="w-full px-4 py-3 3xl:px-5 3xl:py-4 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none text-slate-900"
                                    />
                                </div>

                                {/* 5. Project / Service Type */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-semibold text-slate-700 mb-2">
                                        <Briefcase className="w-4 h-4 3xl:w-5 3xl:h-5 text-blue-600" />
                                        Project / Service Type *
                                    </label>
                                    <select
                                        name="projectType"
                                        value={formData.projectType}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 3xl:px-5 3xl:py-4 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none text-slate-900 bg-white"
                                    >
                                        <option value="">Select your project / service type</option>
                                        {projectTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Honeypot Field */}
                                <div className="hidden">
                                    <label>Website</label>
                                    <input
                                        type="text"
                                        name="website"
                                        value={formData.website || ''}
                                        onChange={handleChange}
                                        tabIndex={-1}
                                        autoComplete="off"
                                    />
                                </div>

                                {/* 6. Your Rating */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-semibold text-slate-700 mb-3">
                                        <Star className="w-4 h-4 3xl:w-5 3xl:h-5 text-blue-600" />
                                        Your Rating *
                                    </label>
                                    <div className="flex gap-2">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <motion.button
                                                key={star}
                                                type="button"
                                                onClick={() => handleRatingClick(star)}
                                                whileHover={{ scale: 1.2 }}
                                                whileTap={{ scale: 0.9 }}
                                                className="focus:outline-none"
                                            >
                                                <Star
                                                    className={`w-10 h-10 3xl:w-12 3xl:h-12 transition-all ${star <= formData.rating
                                                        ? 'text-yellow-400 fill-yellow-400'
                                                        : 'text-slate-300'
                                                        }`}
                                                />
                                            </motion.button>
                                        ))}
                                    </div>
                                    <p className="text-sm 3xl:text-base text-slate-600 mt-2">
                                        {formData.rating === 5 && '⭐ Excellent!'}
                                        {formData.rating === 4 && '👍 Very Good'}
                                        {formData.rating === 3 && '😊 Good'}
                                        {formData.rating === 2 && '🤔 Fair'}
                                        {formData.rating === 1 && '😕 Needs Improvement'}
                                    </p>
                                </div>

                                {/* 7. Review Message */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-semibold text-slate-700 mb-2">
                                        <MessageSquare className="w-4 h-4 3xl:w-5 3xl:h-5 text-blue-600" />
                                        Review Message *
                                    </label>
                                    <textarea
                                        name="review"
                                        value={formData.review}
                                        onChange={handleChange}
                                        required
                                        rows={6}
                                        maxLength={400}
                                        placeholder="Share your experience working with us. What did you like most? How did we help your business?"
                                        className="w-full px-4 py-3 3xl:px-5 3xl:py-4 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl rounded-xl border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none text-slate-900 resize-none"
                                    />
                                    <div className="flex justify-between items-center mt-1">
                                        <p className="text-xs 3xl:text-sm text-slate-500">
                                            Their feedback, experience, testimonial
                                        </p>
                                        <div className="flex gap-2 text-xs 3xl:text-sm">
                                            <p className={`${formData.review.length >= 350 ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>
                                                {formData.review.length} / 400
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* 8. Checkbox with explicit ID for label linking */}
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        name="agreeToPublish"
                                        id="agreeToPublish"
                                        checked={formData.agreeToPublish}
                                        onChange={handleChange}
                                        required
                                        className="w-5 h-5 rounded border-2 border-slate-300 text-blue-600 focus:ring-blue-500 transition-all cursor-pointer"
                                    />
                                    <label htmlFor="agreeToPublish" className="text-sm 3xl:text-base 4xl:text-lg text-slate-700 cursor-pointer select-none">
                                        I agree to allow ZovoX Technologies to publish my review.
                                    </label>
                                </div>

                                {/* Submit Button */}
                                <motion.button
                                    type="submit"
                                    disabled={isSubmitting}
                                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                                    className={`w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold px-8 py-4 3xl:py-5 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                                        }`}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-5 h-5 3xl:w-6 3xl:h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-5 h-5 3xl:w-6 3xl:h-6" />
                                            Submit Review
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </motion.form>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default SubmitReview;
